import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ImageComponent from '../../../common/Image';
import Loader from '../../../common/Loader';
import { useDispatch } from 'react-redux';
import { editedKycSubmit, employerSearch, etbSummary, pincodeSearch, sendEmailOtp, sendMobileOtp, setStage, setSubStage, verifyEmailOtp, verifyMobileOtp } from '../../../../slices/eligibility/eligibilitySlice';
import { toast } from 'react-toastify';
import employementConfig from './employement_config.json';
import { renderFormElements } from './EmploymentForm';

// Mock toast (replace with your actual toast library, e.g., react-toastify)

// Debounce function (generic utility)
const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), delay);
    };
};

function Summary() {
    const [etbData, setEtbData] = useState();
    const dispatch = useDispatch();
    const [loader, setLoader] = useState(false);
    const [popup, setPopup] = useState(false);
    const Ref = useRef(null);
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [timer, setTimer] = useState("00:00:00");
    const [showModal, setShowModal] = useState(false);
    const [showMobileModal, setShowMobileModal] = useState(false);
    // New states for employer search
    const [employerSearchTerm, setEmployerSearchTerm] = useState('');
    const [employerSearchResults, setEmployerSearchResults] = useState([]);
    const [showEmployerSuggestions, setShowEmployerSuggestions] = useState(false);
    const href = "#"
    const [verifiedData, setVerifiedData] = useState(etbData?.e_mail || '')
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const urlParams = new URLSearchParams(window.location.search);
    const referenceId = urlParams.get('referenceId');

    const fetchProductData = useCallback(async () => {
        if (!etbData) {
            setLoader(true);
            try {
                let response = await dispatch(etbSummary({ reference_id: referenceId }));
                if (response?.payload?.response_code === 'S100') {
                    setEtbData(response?.payload?.response_data);
                    // toast.success('Data fetched successfully');
                } else {
                    !response?.payload?.error &&
                        toast.error(response?.payload?.response_string);
                }
            } catch (error) {
                toast.error('An error occurred while fetching data');
            } finally {
                setLoader(false);
            }
        }
    }, [etbData, referenceId, dispatch]);

    useEffect(() => {
        fetchProductData();
    }, [fetchProductData]);

    const employmentInitialValues = useMemo(() => {
        if (!etbData) {
            return {}; // or return null if you prefer
        }
        const values = {};
        employementConfig.fields.manual.forEach(field => {
            if (field.element === 'group') {
                field.fields.forEach(subField => {
                    values[subField.name] = etbData?.[subField.name] || '';
                });
            } else {
                values[field.name] = etbData?.[field.name] || '';
            }
        });
        return values;
    }, [etbData]);
    const employmentValidationSchema = useMemo(() => {
        const schema = {};

        const createConditionFunction = (conditionArray, watchedKeys) => {
            return (...args) => {
                if (!Array.isArray(conditionArray)) return false;

                // Yup passes watched key values as positional args.
                // Build a lookup map: { fieldName: value } from watchedKeys + args.
                // Also fall back to context.parent (last arg) for fields not in watchedKeys.
                const context = args[args.length - 1];
                const parentValues = (context && context.parent) ? context.parent : {};
                const argsMap = {};
                if (watchedKeys && Array.isArray(watchedKeys)) {
                    watchedKeys.forEach((k, i) => { argsMap[k] = args[i]; });
                }
                const resolveField = (key) => {
                    const fieldKey = key.includes(':') ? key.split(':')[1] : key;
                    if (argsMap.hasOwnProperty(fieldKey)) return argsMap[fieldKey];
                    return parentValues[fieldKey];
                };

                const [key1, val1, op1, key2, val2, op2, logicalOp] = conditionArray;

                const evaluateSingleCondition = (key, val, op) => {
                    if (!key || !op) return true;
                    const fieldValue = resolveField(key);
                    switch (op) {
                        case 'eq': return fieldValue === val;
                        case 'neq': return fieldValue !== val;
                        case 'notNull': return fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
                        case 'gt': return parseFloat(fieldValue) > parseFloat(val);
                        case 'gte': return parseFloat(fieldValue) >= parseFloat(val);
                        case 'lte': return parseFloat(fieldValue) <= parseFloat(val);
                        default: return true;
                    }
                };

                const result1 = evaluateSingleCondition(key1, val1, op1);

                if (logicalOp) {
                    const result2 = evaluateSingleCondition(key2, val2, op2);
                    return logicalOp === '&&' ? result1 && result2 : result1 || result2;
                }

                return result1;
            };
        };

        employementConfig.fields.manual.forEach(field => {
            if (field.element === 'group') {
                const groupFields = {};
                field.fields.forEach(subField => {
                    groupFields[subField.name] = Yup.string().optional();
                });

                let groupValidator = Yup.object().shape(groupFields);

                // summary.js — inside the field.validations?.forEach(validation => { ... }) block
                // for the "experience" group field, add an else-if alongside the existing check

                field.validations?.forEach(validation => {
                    if (validation.type === 'test' && validation.name === 'months or years greater than 0') {
                        groupValidator = groupValidator.test(
                            'months-or-years-greater-than-zero',
                            validation.errorMessage,
                            function () { //NOSONAR
                                const parent = this.parent || {}; //NOSONAR
                                const years = parseInt(parent.business_years || 0, 10);
                                const months = parseInt(parent.business_months || 0, 10);

                                if (years === 0 && months === 0) {
                                    //NOSONAR
                                    return this.createError({ //NOSONAR
                                        path: `business_years`,
                                        message: validation.errorMessage,
                                    });
                                }

                                return true;
                            }
                        );
                    } else if (validation.type === 'test' && validation.name === 'minimum experience eligibility') {
                        groupValidator = groupValidator.test(
                            'minimum-experience-eligibility',
                            validation.errorMessage,
                            function () { //NOSONAR
                                const parent = this.parent || {}; //NOSONAR

                                const years = parseInt(parent.business_years || 0, 10);
                                const months = parseInt(parent.business_months || 0, 10);
                                const experienceMonths = (years * 12) + months;

                                const dobRaw = parent.dob;
                                if (!dobRaw) return true;

                                const dob = new Date(dobRaw);
                                if (isNaN(dob.getTime())) return true;

                                const eligibleSince = new Date(dob);
                                eligibleSince.setFullYear(eligibleSince.getFullYear() + 18);

                                const today = new Date();
                                let eligibleExperienceMonths =
                                    (today.getFullYear() - eligibleSince.getFullYear()) * 12 +
                                    (today.getMonth() - eligibleSince.getMonth());

                                if (today.getDate() < eligibleSince.getDate()) {
                                    eligibleExperienceMonths -= 1;
                                }

                                eligibleExperienceMonths = Math.max(0, eligibleExperienceMonths);

                                if (experienceMonths > eligibleExperienceMonths) {
                                    return this.createError({ //NOSONAR
                                        path: `business_years`,
                                        message: validation.errorMessage,
                                    });
                                }

                                return true;
                            }
                        );
                    }
                });

                if (field.displayCondition) {
                    schema[field.name] = Yup.object().when(field.displayCondition.keys, {
                        is: (...args) => createConditionFunction(field.displayCondition.condition, field.displayCondition.keys)(...args),
                        then: () => groupValidator,
                        otherwise: () => Yup.object().optional()
                    });
                } else {
                    schema[field.name] = groupValidator;
                }

            } else {
                let validator = Yup.string().optional();

                const applyValidations = (validations, currentValidator) => {
                    validations.forEach(validation => {
                        switch (validation.type) {
                            case 'required':
                                currentValidator = currentValidator.required(validation.errorMessage);
                                break;
                            case 'length':
                                currentValidator = currentValidator.length(validation.value, validation.errorMessage);
                                break;
                            case 'test':
                                currentValidator = currentValidator.test(
                                    validation.name,
                                    validation.errorMessage,
                                    function (value) {
                                        //NOSONAR
                                        const contextValues = this?.parent || {}; //NOSONAR

                                        const condition = validation.condition || [];
                                        const applyMap = validation.apply || {};

                                        const parseIncomeRange = (val) => {
                                            if (!val) return 0;
                                            val = val.replace(/,/g, '').trim();
                                            const numbers = val.match(/\d+/g);
                                            if (!numbers) return 0;
                                            if (val.includes('Above')) {
                                                return parseFloat(numbers[0]);
                                            }
                                            return parseFloat(numbers[numbers.length - 1]);
                                        };

                                        const resolveValue = (key) => {
                                            let rawVal;

                                            if (key === 'value') {
                                                rawVal = value;
                                            } else {
                                                rawVal = contextValues?.[key];
                                            }

                                            const transform = applyMap[key];
                                            if (transform === 'parseIncomeRange.max') {
                                                return parseIncomeRange(rawVal);
                                            }

                                            return rawVal;
                                        };

                                        const [leftKey, rightKey, operator] = condition;

                                        const left = resolveValue(leftKey);
                                        const right = resolveValue(rightKey);

                                        const numLeft = parseFloat(left);
                                        const numRight = parseFloat(right);

                                        if (isNaN(numLeft) || isNaN(numRight)) return true;

                                        switch (operator) {
                                            case 'gte': return numLeft >= numRight;
                                            case 'lte': return numLeft <= numRight;
                                            case 'gt': return numLeft > numRight;
                                            case 'lt': return numLeft < numRight;
                                            case 'eq': return numLeft === numRight;
                                            case 'neq': return numLeft !== numRight;
                                            default: return true;
                                        }
                                    }
                                );
                                break;
                        }
                    });

                    return currentValidator;
                };

                if (field.validations) {
                    field.validations.forEach(validation => {
                        if (validation.type === 'when') {
                            validator = validator.when(validation.keys, {
                                is: (...args) => createConditionFunction(validation.condition, validation.keys)(...args),
                                then: () => applyValidations(validation.validations, Yup.string().optional())
                            });
                        } else {
                            validator = applyValidations([validation], validator);
                        }
                    });
                }

                if (field.name.includes('.')) {
                    const [parent, child] = field.name.split('.');
                    if (!schema[parent]) {
                        schema[parent] = Yup.object().shape({});
                    }
                    schema[parent] = schema[parent].shape({
                        [child]: validator
                    });
                } else {
                    schema[field.name] = validator;
                }
            }
        });

        return schema;
    }, [employementConfig]);


    const initialValues = useMemo(() => {
        // Compute employment_type default
        let employment_type = (etbData?.employment_type || employmentInitialValues?.employment_type || 'salaried');
        return {
            e_mail: etbData?.e_mail || '',
            gender: etbData?.gender || 'male',
            education_qualification: etbData?.education_qualification || '',
            marital_status: etbData?.marital_status || 'single',
            dob: etbData?.dob || '',
            fathers_first_name: etbData?.fathers_first_name || '',
            fathers_middle_name: etbData?.fathers_middle_name || '',
            fathers_last_name: etbData?.fathers_last_name || '',
            mothers_first_name: etbData?.mothers_first_name || '',
            mothers_middle_name: etbData?.mothers_middle_name || '',
            mothers_last_name: etbData?.mothers_last_name || '',
            spouse_first_name: etbData?.spouse_first_name || '',
            spouse_middle_name: etbData?.spouse_middle_name || '',
            spouse_last_name: etbData?.spouse_last_name || '',
            differently_abled_status: etbData?.differently_abled_status || 'no',
            type_of_impairment: etbData?.type_of_impairment || '',
            percentage_of_impairment: etbData?.percentage_of_impairment || '',
            udid_number: etbData?.udid_number || '',
            residing_same_address: etbData?.residing_same_address || 'yes',
            relationship: etbData?.relationship || 'father',
            alternate_mobile_no: etbData?.alternate_mobile_no || '',
            emailVerified: etbData?.emailVerified || false,
            mobileNoVerified: etbData?.mobileNoVerified || false,
            ...employmentInitialValues,
            employment_type,
            designation:
                etbData?.employment_type === "self-employed"
                    ? etbData?.business_designation ?? etbData?.designation ?? ""
                    : etbData?.designation ?? etbData?.business_designation ?? ""
        };
    }, [etbData, employmentInitialValues]);

    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            // Always required field
            residing_same_address: Yup.string().required('Confirmation is required'),

            // Conditionally required fields based on residing_same_address
            gender: Yup.string().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.string().required('Gender is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            education_qualification: Yup.string().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.string().required('Education is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            marital_status: Yup.string().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.string().required('Marital Status is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            fathers_first_name: Yup.string().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.string().required('Father\'s First Name is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            e_mail: Yup.string().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.string().email('Invalid email').required('Email ID is required'),
                otherwise: () => Yup.string().email('Invalid email').notRequired()
            }),

            alternate_mobile_no: Yup.string().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.string().matches(/^[6-9]\d{9}$/, 'Invalid mobile number').required('Alternate Mobile No. is required'),
                otherwise: () => Yup.string().matches(/^[6-9]\d{9}$/, 'Invalid mobile number').notRequired()
            }),
            type_of_impairment: Yup.string().when('differently_abled_status', {
                is: 'yes',
                then: () => Yup.string().required('Type of impairment is required'),
                otherwise: () => Yup.string().notRequired()
            }),
            percentage_of_impairment: Yup.string().when('differently_abled_status', {
                is: 'yes',
                then: () => Yup.string().required('Percentage of impairment is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            // udid_number: Yup.string().when('differently_abled_status', {
            //     is: 'yes',
            //     then: () => Yup.string()
            //     .matches(/^[A-Z0-9]{18}$/, 'UDID must be 18 characters (A-Z, 0-9)').required('UDID Number is required'),
            //     otherwise: () => Yup.string().notRequired()
            // }),

            relationship: Yup.string().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.string().required('Relationship is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            spouse_first_name: Yup.string().optional(),
            spouse_last_name: Yup.string().optional(),

            emailVerified: Yup.boolean().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.boolean().oneOf([true], 'Email Verification is required'),
                otherwise: () => Yup.boolean().notRequired()
            }),

            mobileNoVerified: Yup.boolean().when('residing_same_address', {
                is: 'yes',
                then: () => Yup.boolean().oneOf([true], 'Alternate Mobile No. Verification is required'),
                otherwise: () => Yup.boolean().notRequired()
            }),

            // Apply conditional validation to employment validation schema
            ...Object.keys(employmentValidationSchema).reduce((acc, key) => {
                acc[key] = Yup.mixed().when('residing_same_address', {
                    is: 'yes',
                    then: () => employmentValidationSchema[key],
                    otherwise: () => Yup.mixed().notRequired()
                });
                return acc;
            }, {}),

            employer_name: Yup.string().when(['employment_type', 'residing_same_address'], {
                is: (employment_type, residing_same_address) =>
                    residing_same_address === 'yes' &&
                    employment_type &&
                    employment_type.toLowerCase() === 'salaried',
                then: () => Yup.string().required('Employer Name is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            employment_profile: Yup.string().when(['employment_type', 'residing_same_address'], {
                is: (employment_type, residing_same_address) =>
                    residing_same_address === 'yes' &&
                    employment_type &&
                    employment_type.toLowerCase() === 'salaried',
                then: () => Yup.string().required('Employer Profile is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            industry_name: Yup.string().when(['employment_type', 'residing_same_address'], {
                is: (employment_type, residing_same_address) =>
                    residing_same_address === 'yes' &&
                    employment_type &&
                    employment_type.toLowerCase() !== 'salaried',
                then: () => Yup.string().required('Business Name is required'),
                otherwise: () => Yup.string().notRequired()
            }),

            business_profile: Yup.string().when(['employment_type', 'residing_same_address'], {
                is: (employment_type, residing_same_address) =>
                    residing_same_address === 'yes' &&
                    employment_type &&
                    employment_type.toLowerCase() !== 'salaried',
                then: () => Yup.string().required('Business Profile is required'),
                otherwise: () => Yup.string().notRequired()
            }),
        })
    }, [employmentValidationSchema]);

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            // This onSubmit will now handle the combined submission
            // Trigger the popup to ask about existing KYC
            if (values?.residing_same_address?.toLowerCase() === 'yes') {
                setDataSubmit()
            }
            else {
                setNewKyc()
            }
        },
    });


    useEffect(() => {
        if (etbData) {
            formik?.setFieldValue("business_years", etbData?.employment_details?.business_years)
            formik?.setFieldValue("business_months", etbData?.employment_details?.business_months)
        }
    }, [etbData])

    const setDataSubmit = async () => {
        setLoader(true);
        try {
            const employmentDetails = {};
            employementConfig.fields.manual.forEach(field => {
                if (field.element === 'group') {
                    field.fields.forEach(subField => {
                        employmentDetails[subField.name] = formik.values[subField.name];
                    });
                } else {
                    employmentDetails[field.name] = formik.values[field.name];
                }
            });

            const payload = {
                personal_details: {
                    e_mail: formik.values.e_mail,
                    gender: formik.values.gender,
                    education_qualification: formik.values.education_qualification,
                    marital_status: formik.values.marital_status,
                    fathers_first_name: formik.values.fathers_first_name,
                    fathers_middle_name: formik.values.fathers_middle_name,
                    fathers_last_name: formik.values.fathers_last_name,
                    mothers_first_name: formik.values.mothers_first_name,
                    mothers_middle_name: formik.values.mothers_middle_name,
                    mothers_last_name: formik.values.mothers_last_name,
                    spouse_first_name: formik.values.spouse_first_name,
                    spouse_middle_name: formik.values.spouse_middle_name,
                    spouse_last_name: formik.values.spouse_last_name,
                    alternate_mobile_no: formik.values.alternate_mobile_no,
                    relationship: formik.values.relationship,
                    differently_abled_status: formik?.values?.differently_abled_status,
                    type_of_impairment: formik?.values?.type_of_impairment,
                    percentage_of_impairment: formik?.values?.percentage_of_impairment,
                    udid_number: formik?.values?.udid_number,
                    residing_same_address: formik.values.residing_same_address,
                    // Include all personal details that are not editable but part of the etbData
                    first_name: etbData?.first_name,
                    last_name: etbData?.last_name,
                    dob: etbData?.dob,
                    mobile_no: etbData?.mobile_no,
                    Adhar_no: etbData?.Adhar_no,
                    pan_no: etbData?.pan_no,
                    res_address_line_1: etbData?.res_address_line_1,
                    res_address_line_2: etbData?.res_address_line_2,
                    res_address_line_3: etbData?.res_address_line_3,
                    res_landmark: etbData?.res_landmark,
                    res_pincode: etbData?.res_pincode,
                    resi_addr_type: etbData?.resi_addr_type,
                    res_city: etbData?.res_city,
                    res_state: etbData?.res_state,
                    per_address_line_1: etbData?.per_address_line_1,
                    per_address_line_2: etbData?.per_address_line_2,
                    per_address_line_3: etbData?.per_address_line_3,
                    per_landmark: etbData?.per_landmark,
                    per_pincode: etbData?.per_pincode,
                    per_city: etbData?.per_city,
                    per_state: etbData?.per_state,
                },
                employment_details: employmentDetails,
                etb_kyc_reuse: true,
                reference_id: referenceId,
            };

            let response = await dispatch(editedKycSubmit(payload)).unwrap();

            if (response?.response_code === 'S100') {
                dispatch(setStage(response?.response_data?.stage));
                dispatch(setSubStage(response?.response_data?.sub_stage));
                // toast.success("Details updated successfully!");
                setPopup(false);
            } else {
                !response?.payload?.error &&
                    toast.error(response?.response_string);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setLoader(false);
        }
    };

    const setNewKyc = async () => {
        setLoader(true);
        try {
            let data = {
                etb_kyc_reuse: false,
                reference_id: referenceId
            };
            let response = await dispatch(editedKycSubmit(data)).unwrap();
            if (response?.response_code === 'S100') {
                dispatch(setStage(response?.response_data?.stage));
                dispatch(setSubStage(response?.response_data?.sub_stage));
                setPopup(false);
            } else {
                !response?.payload?.error &&
                    toast.error(response?.response_string);
            }
        } catch (error) {
            console.error("Submission error for new KYC:", error);
            toast.error("An error occurred while setting new KYC.");
        } finally {
            setLoader(false);
        }
    };

    // This function will be called by the "Proceed" button
    const handleProceed = () => {
        // Manually set all fields as touched to trigger all validations before submitting
        const allFields = Object.keys(formik.initialValues);
        let allTouched = {};
        allFields.forEach(field => {
            allTouched[field] = true;
        });
        formik.setTouched(allTouched, true);

        formik.submitForm(); // This will trigger formik.onSubmit if validation passes
    };

    const sendOtp = async () => {
        if ((/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/).test(formik?.values?.e_mail)) {
            setLoader(true);
            try {
                let data = {
                    "e_mail": formik?.values?.e_mail,
                    "reference_id": referenceId,
                    "type": "send_otp"
                }
                const response = await dispatch(sendEmailOtp(data))
                if (response?.payload?.response_code === 'S100') {
                    setOtpValues(['', '', '', '', '', ''])
                    setShowModal(true)
                    setTimer("01:00");
                    clearTimer(getDeadTime());
                }
                else {
                    !response?.payload?.error &&
                        toast.error(response?.payload?.response_string, {
                            position: 'top-right',
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined
                        });
                }
            } finally {
                setLoader(false); // Add loader end
            }
        } else {
            toast.error((formik?.values?.mobileNo ? 'Please enter a valid Email ID' : "Please enter a Email ID to verify"), {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined
            });
        }
    }

    const verifyOtp = async () => {
        setLoader(true);
        let data = {
            "reference_id": referenceId,
            "type": "verify",
            "code": otpValues?.join(''),
            "e_mail": formik?.values?.e_mail,
        }
        try {
            const response = await dispatch(verifyEmailOtp(data))
            if (response?.payload?.response_code === 'S100') {
                setShowModal(false)
                formik?.setFieldValue("emailVerified", true)
            }
            else {
                !response?.payload?.error &&
                    toast.error(response?.payload?.response_string, {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined
                    });
            }
        } finally {
            setLoader(false); // Add loader end
        }
    }

    const getTimeRemaining = (e) => {
        const total = Date.parse(e) - Date.parse(new Date());  // Get total remaining time in milliseconds
        const seconds = Math.floor((total / 1000) % 60);  // Remaining seconds
        const minutes = Math.floor((total / 1000 / 60) % 60);  // Remaining minutes

        return { total, minutes, seconds };
    };

    // Function to start the timer and update the state
    const startTimer = (e) => {
        let { total, minutes, seconds } = getTimeRemaining(e);
        if (total >= 0) {
            // Update the timer if time is still remaining
            setTimer(
                (minutes > 9 ? minutes : "0" + minutes) + ":" + (seconds > 9 ? seconds : "0" + seconds)
            );
        } else {
            // If time is over, clear the interval
            clearInterval(Ref.current);
        }
    };

    // Function to clear the previous interval and start a new one
    const clearTimer = (e) => {
        setTimer("01:00");  // Reset timer to 120 seconds (2 minutes)

        // Clear the previous interval (if any)
        if (Ref.current) clearInterval(Ref.current);

        // Set a new interval to update the timer every second
        const id = setInterval(() => {
            startTimer(e);
        }, 1000);
        Ref.current = id;  // Store the interval ID in Ref
    };

    // Function to get the deadline (current time + 120 seconds)
    const getDeadTime = () => {
        let deadline = new Date();
        deadline.setSeconds(deadline.getSeconds() + 60);  // Add 120 seconds (2 minutes)
        return deadline;
    };

    const fetchEmployers = useCallback(async (query) => {
        if (query.length >= 3) { // Trigger after 3 letters
            setLoader(true);
            try {
                const response = await dispatch(employerSearch({ "search": query }));
                if (response?.payload?.response_code === 'S100' && response?.payload?.response_data) {
                    setEmployerSearchResults(response.payload.response_data);
                    setShowEmployerSuggestions(true);
                    if (response.payload.response_data.length === 0) {
                        toast.info("No employers found matching your search.");
                    }
                } else {
                    setEmployerSearchResults([]); // Clear results on API error or no data
                    setShowEmployerSuggestions(false);
                    !response?.payload?.error &&
                        toast.error(response?.payload?.response_string || "Error fetching employer names.");
                }
            } catch (error) {
                toast.error("An error occurred while fetching employer names.");
                console.error("Employer search API error:", error);
            } finally {
                setLoader(false);
            }
        } else {
            setEmployerSearchResults([]);
            setShowEmployerSuggestions(false);
        }
    }, [dispatch]);

    // Debounced version of fetchEmployers
    const debouncedFetchEmployers = useMemo(() => debounce(fetchEmployers, 700), [fetchEmployers]);

    const handleEmployerSearchChange = (event) => {
        const value = event.target.value;
        formik.setFieldValue('employer_name', value);
        debouncedFetchEmployers(value);
    };

    const handleEmployerSelect = (employer) => {
        formik.setFieldValue('employer_name', employer);
        setEmployerSearchResults([]);
        setShowEmployerSuggestions(false);
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.employer-search-container')) return;
            setShowEmployerSuggestions(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleOtpChange = (index, value) => {
        // Allow only digits
        if (!/^\d*$/.test(value)) return;

        const newOtpValues = [...otpValues];
        newOtpValues[index] = value.slice(-1); // Ensure only the last digit is used
        setOtpValues(newOtpValues);

        // Auto-move to next input
        if (value && index < otpValues.length - 1) {
            const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        const key = e.key;

        if (key === 'Backspace') {
            if (otpValues[index]) {
                // Clear current value
                const newOtpValues = [...otpValues];
                newOtpValues[index] = '';
                setOtpValues(newOtpValues);
            } else if (index > 0) {
                // Move focus to previous input
                const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
                if (prevInput) prevInput.focus();
            }
        }

        if (key === 'ArrowLeft' && index > 0) {
            const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
            if (prevInput) prevInput.focus();
        }

        if (key === 'ArrowRight' && index < otpValues.length - 1) {
            const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('text').trim().replace(/\D/g, ''); // numbers only
        if (paste.length === otpValues.length) {
            setOtpValues(paste.split(''));
            // Optional: move focus to last input
            const lastInput = document.querySelector(`input[data-index="${otpValues.length - 1}"]`);
            if (lastInput) lastInput.focus();
        }
    };

    const [otpIds] = React.useState(() =>
        otpValues.map(() => crypto.randomUUID()) // or some unique ID generator
    );

    const { values, setFieldValue } = formik;

    // useEffect hook to handle the pincode API call
    useEffect(() => {
        // Check if the pincode field has a value and is 6 characters long
        if (values?.emp_pincode && values?.emp_pincode.length === 6) {
            const fetchPincodeDetails = async () => {
                try {
                    // Replace with your actual pincode API endpoint
                    const response = await dispatch(pincodeSearch({ pincode: values?.emp_pincode, reference_id: referenceId })).unwrap();
                    if (response?.response_code === "S100") {
                        // Assuming the API response has city and state fields
                        if (response?.response_data && response?.response_data.City && response?.response_data.State) {
                            await setFieldValue('emp_city', response?.response_data.City, false);
                            await setFieldValue('emp_state', response?.response_data.State, false);
                            // Mark emp_city as touched and validate
                            if (formik.setFieldTouched) {
                                formik.setFieldTouched('emp_city', true, false);
                            }
                            if (formik.validateField) {
                                formik.validateField('emp_city');
                            }
                        } else {
                            // Clear fields if no data is found
                            setFieldValue('emp_city', '');
                            setFieldValue('emp_state', '');
                        }
                    }
                } catch (error) {
                    console.error("Error fetching pincode details:", error);
                    setFieldValue('emp_city', '');
                    setFieldValue('emp_state', '');
                }
            };

            fetchPincodeDetails();
        } else if (values?.emp_pincode?.length < 6) {
            // Clear city and state if pincode is not 6 digits
            setFieldValue('emp_city', '');
            setFieldValue('emp_state', '');
        }
    }, [values?.emp_pincode, setFieldValue]);

    const sendAlternateMobileOtp = async () => {
        if ((/^[6-9]\d{9}$/).test(formik?.values?.alternate_mobile_no)) {
            setLoader(true);
            try {
                let data = {
                    "mobile_number": formik?.values?.alternate_mobile_no,
                    "reference_id": referenceId,
                    "type": "send_otp"
                }
                const response = await dispatch(sendMobileOtp(data))
                if (response?.payload?.response_code === 'S100') {
                    setOtpValues(['', '', '', '', '', ''])
                    setShowMobileModal(true)
                    setTimer("01:00");
                    clearTimer(getDeadTime());
                }
                else {
                    !response?.payload?.error &&
                        toast.error(response?.payload?.response_string, {
                            position: 'top-right',
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined
                        });
                }
            } finally {
                setLoader(false); // Add loader end
            }
        } else {
            toast.error((formik?.values?.mobileNo ? 'Please enter a valid Mobile Number' : "Please enter a Mobile Number to verify"), {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined
            });
        }
    }

    const verifyAlternateMobileOtp = async () => {
        setLoader(true);
        let data = {
            "mobile_number": formik?.values?.alternate_mobile_no,
            "reference_id": referenceId,
            "type": "verify_otp",
            "otp": otpValues.join('')
        }

        try {
            const response = await dispatch(verifyMobileOtp(data))
            if (response?.payload?.response_code === 'S100') {
                setShowMobileModal(false)
                formik?.setFieldValue("mobileNoVerified", true)
            }
            else {
                !response?.payload?.error &&
                    toast.error(response?.payload?.response_string, {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined
                    });
            }
        } finally {
            setLoader(false); // Add loader end
        }
    }

    const formatMobileNumber = (mobileNo) => {
        if (!mobileNo || mobileNo.length !== 10) return mobileNo;
        return `+91 ${mobileNo.substring(0, 5)} ${mobileNo.substring(5)}`;
    };

    const handleAbledChange = (e) => {
        const { value } = e.target;
        if (value === 'yes') {
            // Trigger the popup instead of updating formik immediately
            setShowConfirmModal(true);
        } else {
            // If "No", just update normally
            formik.setFieldValue('differently_abled_status', 'no');
        }
    };

    const handleConfirmYes = () => {
        formik.setFieldValue('differently_abled_status', 'yes');
        setShowConfirmModal(false);
    };

    // summary.js - inside function Summary()
    const debouncedEmployerSearch = useCallback(
        debounce((searchTerm) => {
            if (searchTerm && searchTerm.length >= 3) {
                console.log("Searching for:", searchTerm); // Debug log
                dispatch(employerSearch({ name: searchTerm }));
            }
        }, 500),
        [dispatch]
    );

    console.log(formik?.errors, "errors", formik?.values, showConfirmModal);

    return (
        <>
            {loader === true && <Loader />}
            <form onSubmit={formik.handleSubmit} className='p-0'>
                <div className="col-md-9 col-sm-12 Side-bank-Right imei-popup">
                    <p className="Pname-i ">KYC Summary Details</p>
                    <div className="dataView box-linear-shadow mt-4">
                        <div className='row gy-4 mx-auto'>
                            <div className="col-12 border-class ps-1">
                                <p className="d-flex justify-content-between kyc_summ_tit"> Personal Information</p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="name" >First Name</label><p >{etbData?.first_name ?? '--'}</p></p>
                            </div>
                            <div className="col-6 pe-1 text-end">
                                <p><label htmlFor="name" >Middle Name</label><p >{etbData?.middle_name ?? "--"}</p></p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="name" >Last Name</label><p >{etbData?.last_name ?? "--"}</p></p>
                            </div>
                            <div className="col-6 pe-1 text-end">
                                <p><label htmlFor="name" >DOB</label><p >{etbData?.dob ?? "--"}</p></p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="name">Mobile numbers</label><p >{etbData?.mobile_no ?? "--"}</p></p>
                            </div>
                            <div className="col-6 pe-1 text-end">
                                <p><label htmlFor="name" > Aadhar Number</label><p >{etbData?.Adhar_no ?? "--"}</p></p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="name">PAN number</label><p>{etbData?.pan_no ?? "--"}</p></p>
                            </div>
                        </div>

                        <div className='row mx-auto mt-4'>
                            <div className="col-12 border-class ps-1 mb-2">
                                <p className="d-flex justify-content-between kyc_summ_tit"> Other Information</p>
                            </div>
                        </div>
                        {/* All fields are now always editable */}
                        <div className='row mx-auto floating-bottom mt-4'>
                            <div className="col-12 ps-1 pe-1">
                                <div className="input-container has-icon">
                                    <input
                                        className="form-control input-underline kyc-summary-input kyc-summary-email-input"
                                        type="text"
                                        id="e_mail"
                                        name="e_mail"
                                        onChange={(e) => {
                                            const newEmail = e.target.value;

                                            // Reset the email field
                                            formik.setFieldValue("e_mail", newEmail);

                                            // If it was previously verified, reset the flag
                                            if (formik.values.emailVerified) {
                                                formik.setFieldValue("emailVerified", false);
                                            }
                                        }}
                                        disabled={formik.values.emailVerified}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.e_mail}
                                        required
                                        placeholder=' '
                                    />
                                    <label className="label form-label emi-label" htmlFor="e_mail">E-mail</label>
                                    <div className="underline"></div>
                                    {formik.touched.e_mail && formik.errors.e_mail ? (
                                        <div className="text-danger small">{formik.errors.e_mail}</div>
                                    ) : null}
                                    {(!formik.errors.e_mail && (formik.errors.emailVerified && formik?.touched?.emailVerified)) && (
                                        <div className="text-danger small">{formik.errors.emailVerified}</div>
                                    )}

                                    {formik?.values?.emailVerified ? (
                                        <i className="icon-Essetional-green-Icon">
                                            <span className="verified">Verified</span>
                                            <span className="path1"></span>
                                            <span className="path2"></span>
                                        </i>
                                    ) : (
                                        <button type="button" className="verify-btn btn-outline-primary summary-verify-btn-1" onClick={() => sendOtp()}>Verify</button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className='row mx-auto floating-bottom'>
                            <div className={`col-6 ps-1 pe-1`}>
                                <label className="form-label mb-1 gender-label" htmlFor="gender">Gender</label>
                                <div className="d-flex gap-2 kyc-genderbg-btn">
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="gender"
                                        id="gender-male"
                                        value="male"
                                        onChange={formik.handleChange}
                                        checked={formik.values.gender === 'male'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0" htmlFor="gender-male">Male</label>

                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="gender"
                                        id="gender-female"
                                        value="female"
                                        onChange={formik.handleChange}
                                        checked={formik.values.gender === 'female'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0" htmlFor="gender-female">Female</label>
                                </div>
                                {/* Error display */}
                                {formik.errors.gender && formik.touched.gender && (
                                    <div className="text-danger">{formik.errors.gender}</div>
                                )}
                            </div>
                        </div>

                        <div className='row mx-auto floating-bottom'>
                            <div className="col-12 ps-1 pe-1">
                                <label className="form-label mb-1 gender-label" htmlFor="marital_status">Marital Status</label>
                                <div className="d-flex gap-2 kyc-genderbg-btn">
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="marital_status"
                                        id="marital-status-single"
                                        value="single"
                                        onChange={formik.handleChange}
                                        checked={formik.values.marital_status === 'single'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0" htmlFor="marital-status-single">Single</label>

                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="marital_status"
                                        id="marital-status-married"
                                        value="married"
                                        onChange={formik.handleChange}
                                        checked={formik.values.marital_status === 'married'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0" htmlFor="marital-status-married">Married</label>
                                </div>
                                {/* Error display */}
                                {formik.errors.marital_status && formik.touched.marital_status && (
                                    <div className="text-danger">{formik.errors.marital_status}</div>
                                )}
                            </div>
                        </div>

                        <div className='row mx-auto floating-bottom'>
                            <div className="col-12 ps-1 pe-1">
                                <label className="form-label mb-1 gender-label" htmlFor="relationship">Relationship with Applicant</label>
                                <div className="d-flex gap-2 kyc-genderbg-btn justify-content-end">
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="relationship"
                                        id="relationship-father"
                                        value="father"
                                        onChange={formik.handleChange}
                                        checked={formik.values.relationship === 'father'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0" htmlFor="relationship-father">Father</label>

                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="relationship"
                                        id="relationship-spouse"
                                        value="spouse"
                                        onChange={formik.handleChange}
                                        checked={formik.values.relationship === 'spouse'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0" htmlFor="relationship-spouse">Spouse</label>
                                </div>
                                {/* Error display */}
                                {formik.errors.relationship && formik.touched.relationship && (
                                    <div className="text-danger">{formik.errors.relationship}</div>
                                )}
                            </div>
                        </div>

                        <div className='row mx-auto floating-top'>
                            <div className="col-12 ps-1 pe-1">
                                <label htmlFor="education_qualification" className='emi-label gender-label mb-0'>Educational Qualification</label>
                                <select
                                    className="form-select input-underline kyc-summary-dropdown"
                                    id="education_qualification"
                                    name="education_qualification"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.education_qualification}
                                >
                                    <option value="">Select</option>
                                    <option value="SSC">SSC</option>
                                    <option value="HSC">HSC</option>
                                    <option value="Under Graduate">Under Graduate</option>
                                    <option value="Post Graduate">Post Graduate</option>
                                    <option value="Professional">Professional</option>
                                    <option value="Other">Other</option>
                                </select>
                                {formik.touched.education_qualification && formik.errors.education_qualification ? (
                                    <div className="text-danger small">{formik.errors.education_qualification}</div>
                                ) : null}
                            </div>
                        </div>

                        <div className='row mx-auto floating-top'>
                            <div className="col-12 ps-1 pe-1">
                                <div className="input-container has-icon">
                                    <input
                                        className="form-control input-underline kyc-summary-input"
                                        type="text"
                                        id="fathers_first_name"
                                        name="fathers_first_name"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.fathers_first_name}
                                        required
                                        placeholder=' '
                                    />
                                    <label htmlFor="fathers_first_name" className="label form-label">Father / Spouse Name</label>
                                    <div className="underline"></div>
                                    {formik.touched.fathers_first_name && formik.errors.fathers_first_name ? (
                                        <div className="text-danger small">{formik.errors.fathers_first_name}</div>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className='row mx-auto floating-bottom'>
                            <div className="col-12 ps-1 pe-1">
                                <div className="input-container has-icon">
                                    <input
                                        className="form-control input-underline kyc-summary-input"
                                        id="alternate_mobile_no"
                                        name="alternate_mobile_no"
                                        type="tel"
                                        maxLength={10}
                                        inputMode="numeric"
                                        autoComplete="off"
                                        onChange={(e) => {
                                            const newEmail = e.target.value;

                                            // Reset the email field
                                            formik.setFieldValue("alternate_mobile_no", newEmail);

                                            // If it was previously verified, reset the flag
                                            if (formik.values.mobileNoVerified) {
                                                formik.setFieldValue("mobileNoVerified", false);
                                            }
                                        }}
                                        disabled={formik.values.mobileNoVerified}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.alternate_mobile_no}
                                        required
                                        placeholder=' '
                                    />
                                    <label className="label form-label" htmlFor="alternate_mobile_no">Alternate Mobile No.</label>
                                    <div className="underline"></div>
                                    {formik.touched.alternate_mobile_no && formik.errors.alternate_mobile_no ? (
                                        <div className="text-danger small">{formik.errors.alternate_mobile_no}</div>
                                    ) : null}
                                    {(!formik.errors.alternate_mobile_no && (formik.errors.mobileNoVerified && formik?.touched?.mobileNoVerified)) && (
                                        <div className="text-danger small">{formik.errors.mobileNoVerified}</div>
                                    )}

                                    {formik?.values?.mobileNoVerified ? (
                                        <i className="icon-Essetional-green-Icon">
                                            <span className="verified">Verified</span>
                                            <span className="path1"></span>
                                            <span className="path2"></span>
                                        </i>
                                    ) : (
                                        <button type="button" className="verify-btn btn-outline-primary summary-verify-btn-2" onClick={() => sendAlternateMobileOtp()}>Verify</button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='row mx-auto floating-top'>
                            <div className="col-12 ps-1">
                                <label className="form-label mb-1 gender-label" htmlFor="differently_abled_status">Differently abled status</label>
                                <div className="d-flex gap-2 kyc-genderbg-btn">
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="differently_abled_status"
                                        id="abled-same-yes"
                                        value="yes"
                                        onChange={handleAbledChange}
                                        checked={formik.values.differently_abled_status === 'yes'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0 fs-16" htmlFor="abled-same-yes">Yes</label>

                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="differently_abled_status"
                                        id="abled-same-no"
                                        value="no"
                                        onChange={formik.handleChange}
                                        checked={formik.values.differently_abled_status === 'no'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0 fs-16" htmlFor="abled-same-no">No</label>
                                </div>
                                {/* Error display */}
                                {formik.errors.residing_same_address && formik.touched.residing_same_address && (
                                    <div className="text-danger">{formik.errors.residing_same_address}</div>
                                )}
                            </div>
                        </div>
                        {formik?.values?.differently_abled_status === 'yes' &&
                            <>
                                <div className='row mx-auto floating-top'>
                                    <div className="col-12 ps-1 pe-1">
                                        <label htmlFor="type_of_impairment" className='emi-label gender-label mb-0'>Type of Impairment</label>
                                        <select
                                            className="form-select input-underline kyc-summary-dropdown"
                                            id="type_of_impairment"
                                            name="type_of_impairment"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.type_of_impairment}
                                        >
                                            <option value="">Select</option>
                                            <option value="acidattackvictim">Acid Attack Victims</option>
                                            <option value="autismdisorder">Autism Spectrum Disorder</option>
                                            <option value="blindness">Blindness</option>
                                            <option value="blooddisorders">Blood Disorders (Thalassemia, Hemophilia, Sickle Cell Disease)</option>
                                            <option value="cerebralpalsy">Cerebral Palsy</option>
                                            <option value="chronicneurological">Chronic Neurological Conditions (like Multiple Sclerosis and Parkinson's
                                                Disease)</option>
                                            <option value="dwarfism">Dwarfism</option>
                                            <option value="hearingimpairment">Hearing Impairment</option>
                                            <option value="intellectualdisability">Intellectual Disability</option>
                                            <option value="leprosycuredpersons">Leprosy-cured Persons</option>
                                            <option value="locomotordisability">Locomotor Disability</option>
                                            <option value="lowvision">Low Vision</option>
                                            <option value="mentalillness">Mental Illness</option>
                                            <option value="multipledisabilities">Multiple Disabilities (including Deaf-Blindness)</option>
                                            <option value="musculardystrophy">Muscular Dystrophy</option>
                                            <option value="specificlearningdisabilities">Specific Learning Disabilities</option>
                                            <option value="speechandlanguagedisability">Speech and Language Disability</option>
                                        </select>
                                        {formik.touched.type_of_impairment && formik.errors.type_of_impairment ? (
                                            <div className="text-danger small">{formik.errors.type_of_impairment}</div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className='row mx-auto floating-top'>
                                    <div className="col-12 ps-1 pe-1">
                                        <label htmlFor="percentage_of_impairment" className='emi-label gender-label mb-0'>Percentage of Impairment</label>
                                        <select
                                            className="form-select input-underline kyc-summary-dropdown"
                                            id="percentage_of_impairment"
                                            name="percentage_of_impairment"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.percentage_of_impairment}
                                        >
                                            <option value="">Select</option>
                                            <option value="lessthan40">Less than 40 Percentage</option>
                                            <option value="greaterthanequalto40">Greater than or equal to 40 Percentage</option>
                                        </select>
                                        {formik.touched.percentage_of_impairment && formik.errors.percentage_of_impairment ? (
                                            <div className="text-danger small">{formik.errors.percentage_of_impairment}</div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className='row mx-auto floating-top'>
                                    <div className="col-12 ps-1 pe-1">
                                        <div className="input-container has-icon">
                                            <input
                                                className="form-control input-underline kyc-summary-input"
                                                type="text"
                                                id="udid_number"
                                                name="udid_number"
                                                onChange={(e) => {
                                                    let value = e.target.value;

                                                    // 1. Convert to uppercase first
                                                    value = value.toUpperCase();

                                                    // 2. Remove anything that is NOT a letter or a number
                                                    // [^...] means "match everything except these"
                                                    value = value.replace(/[^A-Z0-9]/g, '');

                                                    // 3. Limit to 18 characters
                                                    if (value.length <= 18) {
                                                        formik.setFieldValue('udid_number', value);
                                                    }
                                                }}
                                                maxLength={18}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.udid_number}
                                                required
                                                placeholder=' '
                                            />
                                            <label htmlFor="udid_number" className="label form-label">UDID Number</label>
                                            <div className="underline"></div>
                                            {formik.touched.udid_number && formik.errors.udid_number ? (
                                                <div className="text-danger small">{formik.errors.udid_number}</div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                        <div className='row mx-auto'>
                            <div className="col-12 ps-1">
                                <label className="form-label mb-1 gender-label" htmlFor="residing_same_address">Customer residing at same address</label>
                                <div className="d-flex gap-2 kyc-genderbg-btn">
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="residing_same_address"
                                        id="residing-same-yes"
                                        value="yes"
                                        onChange={formik.handleChange}
                                        checked={formik.values.residing_same_address === 'yes'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0 fs-16" htmlFor="residing-same-yes">Yes</label>

                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="residing_same_address"
                                        id="residing-same-no"
                                        value="no"
                                        onChange={formik.handleChange}
                                        checked={formik.values.residing_same_address === 'no'}
                                    />
                                    <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0 fs-16" htmlFor="residing-same-no">No</label>
                                </div>
                                {/* Error display */}
                                {formik.errors.residing_same_address && formik.touched.residing_same_address && (
                                    <div className="text-danger">{formik.errors.residing_same_address}</div>
                                )}
                            </div>
                        </div>

                        <div className='row mx-auto mt-2 gy-4'>
                            <div className="col-12 border-class ps-1">
                                <p className="d-flex justify-content-between kyc_summ_tit"> Residence Details</p>
                            </div>
                            <div className="col-12 ps-1 pe-1">
                                <p><label htmlFor="res_address">Residence Address</label><p>
                                    {`${etbData?.res_address_line_1 || '--'}, ${etbData?.res_address_line_2 || ''}, ${etbData?.res_address_line_3}, ${etbData?.res_city || '--'}, ${etbData?.res_state || '--'}, ${etbData?.res_pincode || '--'}`.replace(/,(\s*,)+/g, ',').replace(/^,/, '').replace(/,$/, '') || '--'}
                                </p></p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="res_landmark">Residence Landmark</label><p>{etbData?.res_landmark ?? "--"}</p></p>
                            </div>
                            <div className="col-6 pe-1 text-end">
                                <p><label htmlFor="name" >Residence Pincode</label><p >{etbData?.res_pincode ?? "--"}</p></p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="name">Residence City</label><p >{etbData?.res_city ?? "--"}</p></p>
                            </div>
                            <div className="col-6 pe-1 text-end">
                                <p><label htmlFor="name" >Residence State</label><p >{etbData?.res_state ?? "--"}</p></p>
                            </div>
                            <div className="col-12 ps-1 pe-1">
                                <label htmlFor="name">Permanent Address</label><p>
                                    {`${etbData?.per_address_line_1 || '--'}, ${etbData?.per_address_line_2 || ''}, ${etbData?.per_address_line_3}, ${etbData?.per_city || ''}, ${etbData?.per_state || ''}, ${etbData?.per_pincode || ''}`.replace(/,(\s*,)+/g, ',').replace(/^,/, '').replace(/,$/, '') || '--'}
                                </p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="name">Landmark</label><p>{etbData?.per_landmark ?? "--"}</p></p>
                            </div>
                            <div className="col-6 pe-1 text-end">
                                <p><label htmlFor="name">Pincode</label><p>{etbData?.per_pincode ?? "--"}</p></p>
                            </div>
                            <div className="col-6 ps-1">
                                <p><label htmlFor="name">City</label><p>{etbData?.per_city ?? "--"}</p></p>
                            </div>
                            <div className="col-6 pe-1 text-end">
                                <p><label htmlFor="name">State</label><p>{etbData?.per_state ?? "--"}</p></p>
                            </div>
                        </div>

                        <div className='row mx-auto mt-4'>
                            <div className="col-12 border-class ps-1">
                                <p className="d-flex justify-content-between kyc_summ_tit"> Occupational Details</p>
                            </div>
                        </div>

                        {/* Render occupational fields in original order, using floating input style for specific fields */}
                        {/* Render occupational fields in original order, using floating input style for specific fields */}
                        {employementConfig.fields.manual.map((field) => {
                            // 1) Respect the field displayCondition (case-insensitive)
                            const evaluateDisplayCondition = (displayCondition) => {
                                if (!displayCondition || !Array.isArray(displayCondition.condition)) return true;
                                const cond = displayCondition.condition;
                                const norm = v => (typeof v === 'string' ? v.toLowerCase() : v);

                                const evalSingle = (arr) => {
                                    const [k, expected, op] = arr;
                                    const actual = formik.values?.[k];
                                    const a = norm(actual);
                                    const b = norm(expected);
                                    if (op === 'eq') return a === b;
                                    if (op === 'neq') return a !== b;
                                    if (op === 'notNull') return actual !== '' && actual !== null && actual !== undefined;
                                    return false;
                                };

                                if (cond.length <= 3) return evalSingle(cond);
                                const c1 = cond.slice(0, 3);
                                const c2 = cond.slice(3, 6);
                                const logical = cond[6];
                                const r1 = evalSingle(c1);
                                const r2 = evalSingle(c2);
                                return logical === '&&' ? (r1 && r2) : (r1 || r2);
                            };

                            if (!evaluateDisplayCondition(field.displayCondition)) return null;

                            // 3) Keep a small explicit list of fields that we want to render as floating-top inputs here.
                            const floatingTopFields = [
                                'emp_address_line_1',
                                'emp_address_line_2',
                                'emp_address_line_3',
                                'emp_landmark'
                            ];

                            if (floatingTopFields.includes(field.name)) {
                                let label = field.label;
                                switch (field.name) {
                                    case 'emp_address_line_1': label = 'Address Line 1'; break;
                                    case 'emp_address_line_2': label = 'Address Line 2'; break;
                                    case 'emp_address_line_3': label = 'Address Line 3'; break;
                                    case 'emp_landmark': label = 'Landmark'; break;
                                    case 'emp_pincode': label = 'Pincode'; break;
                                    default: label = field.label;
                                }
                                return (
                                    <div className='row mx-auto floating-top' key={field.name}>
                                        <div className="col-12 ps-1 pe-1">
                                            <div className="input-container has-icon">
                                                <input
                                                    className="form-control input-underline kyc-summary-input"
                                                    type="text"
                                                    id={field.name}
                                                    name={field.name}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values[field.name]}
                                                    required
                                                    placeholder=' '
                                                />
                                                <label htmlFor={field.name} className="label form-label emi-label">{label}</label>
                                                <div className="underline"></div>
                                                {formik.touched[field.name] && formik.errors[field.name] ? (
                                                    <div className="text-danger small">{formik.errors[field.name]}</div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            // 4) For everything else, delegate to renderFormElements. This respects:
                            //    - displayCondition (inside renderFormElements)
                            //    - the special floating-bottom handling for employer_name (so employer_name is shown only when it should).
                            return renderFormElements([field], formik, {
                                handleEmployerSearchChange,
                                handleEmployerSelect,
                                employerSearchResults,
                                showEmployerSuggestions
                            });
                        })}
                    </div>

                    {showModal &&
                        <>
                            <div className="modal fade show" id="mobileOtp" aria-labelledby="mobileOtp" tabIndex="-1" style={{ display: "block", padding: "0px" }} aria-modal="true"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setShowModal(false)
                                    }
                                }}
                                onClick={() => setShowModal(false)}
                            >
                                <div
                                    className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal"
                                >

                                    <div className="modal-content"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.stopPropagation()
                                            }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="modal-body mt-3 mb-3">
                                            <div className="row">
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3 d-flex justify-content-between align-items-start">
                                                    <ImageComponent fileName="eligibleotp.svg" alt="eligimob" />
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                                    <p className="form-label fw-semibold fs-6">Please enter OTP</p>
                                                    <p className="form-label fs-7">
                                                        An 6 digit code has been sent to :
                                                    </p>

                                                    <div className="mt-2">
                                                        <p className="fw-semibold form-label fs-7">
                                                            {formik?.values?.e_mail}
                                                        </p>
                                                    </div>

                                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-3">
                                                        <div>
                                                            <div className="d-flex gap-3 justify-content-center mb-4">
                                                                {otpValues.map((value, index) => (
                                                                    <input
                                                                        key={otpIds[index]}
                                                                        type="tel"
                                                                        maxLength="1"
                                                                        value={value}
                                                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                                        onPaste={handleOtpPaste}
                                                                        data-index={index}
                                                                        className="otp-in input-underline otp-input"
                                                                        inputMode="numeric"
                                                                        autoComplete="one-time-code"
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='head-title mt-3'>
                                                        {timer !== "00:00" ? (
                                                            `Resend OTP in ${timer} seconds`
                                                        ) : (
                                                            <a
                                                                href={href}
                                                                tabIndex={0}
                                                                className="anchor-btn"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    sendOtp();
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        sendOtp();
                                                                    }
                                                                }}
                                                            >
                                                                Resend
                                                            </a>
                                                        )}
                                                        <br></br>
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                                    <button
                                                        className="btn btn-primary btn-lg w-100 signin"
                                                        type="button"
                                                        onClick={() => { verifyOtp() }}
                                                    >
                                                        Verify
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-backdrop fade show"></div>
                        </>
                    }

                    {showMobileModal &&
                        <>
                            <div className="modal fade show" id="emailOtp" aria-labelledby="emailOtp" tabIndex="-1" style={{ display: "block" }} aria-modal="true"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setShowMobileModal(false)
                                    }
                                }}
                                onClick={() => setShowMobileModal(false)}>
                                <div
                                    className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.stopPropagation()
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >

                                    <div className="modal-content">
                                        <div className="modal-body otp-modal mt-3 mb-3">
                                            <div className="row">
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3 d-flex justify-content-between align-items-start">
                                                    <ImageComponent fileName="eligibleotp.svg" alt="eligimob" />
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                                    <p className="form-label fw-semibold fs-6">Please Enter OTP Sent to:</p>
                                                    <p className="form-label d-none fs-7">
                                                        A 6 digit code has been sent to :
                                                    </p>

                                                    <div className="mt-2">
                                                        <p className="fw-semibold form-label fs-7">
                                                            {formatMobileNumber(formik?.values?.alternate_mobile_no)}
                                                        </p>
                                                    </div>

                                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-3">
                                                        <div>
                                                            <div className="d-flex gap-3 justify-content-center mb-4">
                                                                {otpValues.map((value, index) => (
                                                                    <input
                                                                        key={otpIds[index]}
                                                                        type="tel"
                                                                        maxLength="1"
                                                                        value={value}
                                                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                                        onPaste={handleOtpPaste}
                                                                        data-index={index}
                                                                        className="otp-in input-underline otp-input"
                                                                        inputMode="numeric"
                                                                        autoComplete="one-time-code"
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='head-title mt-3'>
                                                        {timer !== "00:00" ? (
                                                            `Resend OTP in ${timer} seconds`
                                                        ) : (
                                                            <a
                                                                href={href}
                                                                tabIndex={0}
                                                                className="anchor-btn"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    sendAlternateMobileOtp();
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        sendAlternateMobileOtp();
                                                                    }
                                                                }}
                                                            >
                                                                Resend
                                                            </a>
                                                        )}
                                                        <br></br>
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                                    <button
                                                        className="btn btn-primary btn-lg w-100 signin"
                                                        type="button"
                                                        onClick={() => { verifyAlternateMobileOtp() }}
                                                    >
                                                        Verify
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-backdrop fade show"></div>
                        </>
                    }

                    {popup &&
                        <>
                            <div className="modal show fade" id="existingcred" tabIndex="-1" style={{ display: "block", paddingLeft: "0px" }} aria-labelledby="existingcred" aria-hidden="true">
                                <div className="modal-dialog bottom-modal">
                                    <div className="modal-content">
                                        <div className="px-3 py-3 text-center">
                                            <ImageComponent fileName="2fverify.svg" />
                                            <p className="fw-semibold m-0 mt-3">Do you wish to proceed with the existing KYC?</p>
                                            <div className="d-flex justify-content-between mt-4">
                                                <button className="btn btn-outline-primary px-5" type="button" onClick={setNewKyc}>No</button>
                                                <button className="btn btn-primary px-5" type="button" onClick={setDataSubmit}>Yes</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-backdrop fade show"></div>
                        </>
                    }
                </div>
                <footer className="sticky-bottom-1">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-sm-12">
                                <div className="d-flex justify-content-end">
                                    {/* Use type="button" to prevent default form submission until validations pass */}
                                    <button className="btn btn-primary" type="button" onClick={handleProceed}>Proceed</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
                {showConfirmModal &&
                    <>
                        <div className="modal show fade" id="existingcred" tabIndex="-1" style={{ display: "block", paddingLeft: "0px" }} aria-labelledby="existingcred" aria-hidden="true">
                            <div className="modal-dialog bottom-modal">
                                <div className="modal-content">
                                    <div className="px-3 py-3 text-center">
                                        <ImageComponent fileName="2fverify.svg" />
                                        <p className="fw-semibold m-0 mt-3">You have selected disability status as “Yes”. Please confirm to proceed with capturing disability detail or cancel to proceed</p>
                                        <div className="d-flex justify-content-between mt-4">
                                            <button className="btn btn-outline-primary px-5" type="button" onClick={() => { setShowConfirmModal(false) }}>Cancel</button>
                                            <button className="btn btn-primary px-5" type="button" onClick={handleConfirmYes}>Confirm</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show"></div>
                    </>
                }
            </form >
        </>
    );
}

export default Summary;